import type { Leaderboard, Meta, ModelDetail } from './types';

// Runtime fetchers against the static JSON published by the pipeline.
// All paths are relative to the deployed site root, which serves /public.
//
// We use plain fetch + manual cache rather than a data library; the dataset
// is small, the site has four routes, and a 30-line cache is honest.

const cache = new Map<string, unknown>();

async function fetchJson<T>(path: string): Promise<T> {
  const cached = cache.get(path);
  if (cached) return cached as T;
  const res = await fetch(path);
  if (!res.ok) {
    throw new Response(`Failed to load ${path}: ${res.status}`, {
      status: res.status,
      statusText: res.statusText,
    });
  }
  const data = (await res.json()) as T;
  cache.set(path, data);
  return data;
}

export function loadMeta(): Promise<Meta> {
  return fetchJson<Meta>('/data/meta.json');
}

export function loadLeaderboard(): Promise<Leaderboard> {
  return fetchJson<Leaderboard>('/data/leaderboard.json');
}

export function loadModel(id: string): Promise<ModelDetail> {
  // The id arrives URL-encoded from the route param. The on-disk filename
  // uses double-dash slug form, e.g. "openai--gpt-5-4". We trust the id as
  // the file slug because that is exactly what the leaderboard exposes.
  const safe = id.replace(/[^a-zA-Z0-9_-]/g, '');
  return fetchJson<ModelDetail>(`/data/models/${safe}.json`);
}

// Loader bundle used by the homepage. We fetch leaderboard + meta + the top
// three ranked model detail files in parallel so the homepage can show real
// featured discourse next to the table without an extra round trip after the
// initial paint.
export interface HomeBundle {
  meta: Meta;
  leaderboard: Leaderboard;
  topModels: ModelDetail[];
}

export async function loadHomeBundle(): Promise<HomeBundle> {
  const [meta, leaderboard] = await Promise.all([loadMeta(), loadLeaderboard()]);
  const topRanked = leaderboard.rows.filter((r) => r.coverage_status === 'ranked').slice(0, 3);
  const topModels = await Promise.all(topRanked.map((row) => loadModel(row.id)));
  return { meta, leaderboard, topModels };
}

export interface ModelPageBundle {
  meta: Meta;
  model: ModelDetail;
}

export async function loadModelBundle(id: string): Promise<ModelPageBundle> {
  const [meta, model] = await Promise.all([loadMeta(), loadModel(id)]);
  return { meta, model };
}

export interface MethodologyBundle {
  meta: Meta;
}

export async function loadMethodologyBundle(): Promise<MethodologyBundle> {
  const meta = await loadMeta();
  return { meta };
}
